
import { supabase } from '@/integrations/supabase/client';
import { StaffUserManagementLog } from './types';
import { canStaffManageUser } from './utils';

// Get user analytics/progress
export const getUserAnalyticsByStaff = async (userId: string) => {
  try {
    // Check if staff can manage this user
    const canManage = await canStaffManageUser(userId);
    if (!canManage) {
      return null;
    }

    // Get user progress data
    const { data: moduleProgress } = await supabase
      .from('user_module_progress')
      .select('*')
      .eq('user_id', userId);

    const { data: assignments } = await supabase
      .from('course_assignments')
      .select('*')
      .eq('user_id', userId);

    const { data: attempts } = await supabase
      .from('module_attempts')
      .select('*')
      .eq('user_id', userId)
      .order('attempted_at', { ascending: false });

    return {
      moduleProgress: moduleProgress || [],
      assignments: assignments || [],
      recentAttempts: attempts?.slice(0, 10) || [],
      totalModulesCompleted: moduleProgress?.filter(m => m.progress === 100).length || 0,
      totalCoursesAssigned: assignments?.length || 0,
      averageProgress: assignments?.length > 0 
        ? assignments.reduce((sum, a) => sum + (a.final_grade || 0), 0) / assignments.length 
        : 0
    };
  } catch (error) {
    console.error('Error fetching user analytics:', error);
    return null;
  }
};

// Get staff action logs
export const getStaffActionLogs = async (targetUserId?: string): Promise<StaffUserManagementLog[]> => {
  try {
    let query = supabase
      .from('staff_user_management_logs')
      .select('*')
      .order('performed_at', { ascending: false })
      .limit(50);

    if (targetUserId) {
      query = query.eq('target_user_id', targetUserId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching staff logs:', error);
      return [];
    }

    // Transform the data to match our interface, handling type conversions properly
    return (data || []).map(log => ({
      id: log.id,
      staff_id: log.staff_id,
      target_user_id: log.target_user_id,
      action_type: log.action_type,
      action_details: (log.action_details && typeof log.action_details === 'object' && log.action_details !== null) 
        ? log.action_details as Record<string, any>
        : {},
      performed_at: log.performed_at,
      ip_address: log.ip_address ? String(log.ip_address) : undefined,
      user_agent: log.user_agent || undefined
    }));
  } catch (error) {
    console.error('Error in getStaffActionLogs:', error);
    return [];
  }
};
